// =============================================================================
// FILE: packages/renderer/src/hooks/useConnectivityMode.ts
// PURPOSE: Detects whether the app is running in Online or Enterprise
//          Stand-Alone mode. Primary signal: navigator.onLine + optional
//          ping to rcd2engine.com. User can also force a mode via prefs.
// =============================================================================

import { useCallback, useEffect, useState } from 'react';

export type ConnMode = 'online' | 'standalone';

export interface ConnStatus {
  mode:        ConnMode;
  checking:    boolean;
  serverReach: boolean | null;   // null = not yet checked
  lastChecked: number | null;
  /** Manually force a mode (persisted in localStorage). */
  forceMode:   (m: ConnMode | 'auto') => void;
}

const R2_PING = 'https://rcd2engine.com/ping';
const POLL_MS = 60_000;
const LS_KEY  = 'bim.conn.forced';

function readForced(): ConnMode | 'auto' {
  const v = localStorage.getItem(LS_KEY);
  if (v === 'online' || v === 'standalone') return v;
  return 'auto';
}

export function useConnectivityMode(): ConnStatus {
  const [forced,      setForced]      = useState<ConnMode | 'auto'>(readForced);
  const [serverReach, setServerReach] = useState<boolean | null>(null);
  const [checking,    setChecking]    = useState(false);
  const [lastChecked, setLastChecked] = useState<number | null>(null);

  const probe = useCallback(async () => {
    if (!navigator.onLine) { setServerReach(false); setLastChecked(Date.now()); return; }
    setChecking(true);
    try {
      await fetch(R2_PING, { method: 'HEAD', mode: 'no-cors', signal: AbortSignal.timeout(5_000) });
      setServerReach(true);
    } catch {
      setServerReach(false);
    } finally {
      setChecking(false);
      setLastChecked(Date.now());
    }
  }, []);

  useEffect(() => {
    void probe();
    const id = setInterval(() => void probe(), POLL_MS);
    const onOnline  = () => void probe();
    const onOffline = () => { setServerReach(false); setLastChecked(Date.now()); };
    window.addEventListener('online',  onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      clearInterval(id);
      window.removeEventListener('online',  onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [probe]);

  const forceMode = useCallback((m: ConnMode | 'auto') => {
    if (m === 'auto') localStorage.removeItem(LS_KEY);
    else              localStorage.setItem(LS_KEY, m);
    setForced(m);
  }, []);

  const autoMode: ConnMode = navigator.onLine && serverReach !== false ? 'online' : 'standalone';
  const mode: ConnMode     = forced === 'auto' ? autoMode : forced;

  return { mode, checking, serverReach, lastChecked, forceMode };
}
